import { useThreadContextInternal, type ThreadContextValue } from '../context/ThreadProvider'

// Track if we've already warned to avoid spam
let hasWarnedAboutArrayUsage = false

/**
 * Hook to access the thread context.
 *
 * Must be used within a ThreadProvider. Returns the full thread context
 * including messages, actions, and file management.
 *
 * @returns The thread context value
 *
 * @example
 * ```tsx
 * function ChatMessages() {
 *   const { messages, sendMessage, files } = useThread()
 *
 *   return (
 *     <div>
 *       {messages.map(msg => <Message key={msg.id} message={msg} />)}
 *       <button onClick={() => sendMessage({ role: 'user', content: 'Hello!' })}>
 *         Send
 *       </button>
 *     </div>
 *   )
 * }
 *
 * // Wrap with ThreadProvider
 * <ThreadProvider threadId="thread-123">
 *   <ChatMessages />
 * </ThreadProvider>
 * ```
 */
export function useThread(): ThreadContextValue {
  // useThreadContextInternal throws if not within ThreadProvider
  const context = useThreadContextInternal()

  // In development, wrap in Proxy to detect old usage pattern
  if (process.env.NODE_ENV !== 'production') {
    const arrayMethods = ['map', 'filter', 'forEach', 'find', 'some', 'every', 'reduce', 'length', 'push', 'pop', 'slice', 'splice']

    return new Proxy(context, {
      get(target, prop) {
        if (arrayMethods.includes(prop as string) && !hasWarnedAboutArrayUsage) {
          hasWarnedAboutArrayUsage = true
          console.warn(
            '[BREAKING CHANGE] useThread() now returns an object, not an array.\n' +
            'Change: const messages = useThread()\n' +
            'To:     const { messages } = useThread()\n\n' +
            'The returned object includes: messages, sendMessage, stopExecution, files, and more.'
          )
        }
        return target[prop as keyof typeof target]
      }
    })
  }

  return context
}
